<template>
  <div>
      <div class="card-header" :class="{'cursor-move': checked}">
          <div class="form-check d-inline-block">
              <input class="form-check-input"
                     type="checkbox"
                     :id="attachmentType"
                     v-model="attachmentTypeModel"
              />
              <label class="form-check-label ms-2 w-100" :for="attachmentType">
                  {{ translateType(attachmentType) }}
              </label>
          </div>
      </div>

      <div class="card-body" v-if="checked">
          <div v-for="name in rejectedNames" :key="name" class="alert alert-danger">
              Typ souboru <strong>{{ name }}</strong> není podporován.
          </div>
          <file-picker v-if="uploadedAttachments.length === 0 && uploads.length === 0"
                       :compact="false"
                       button-label="Přidat soubory"
                       button-class="btn-outline-primary btn-sm"
                       :accepted-types="SUPPORTED_ATTACHMENT_TYPES"
                       @selected="filesSelected"
          >
              <span class="fw-semibold d-block">Přetáhněte sem soubory přílohy</span>
              <span class="text-muted small d-block">nebo klikněte a vyberte je</span>
              <span class="text-muted small">Obrázky nebo PDF nejvýš o {{ MAX_PDF_PAGES }} stranách, každá strana PDF bude samostatný soubor.</span>
          </file-picker>
          <div v-else class="d-flex flex-wrap align-items-center gap-3 mb-3">
              <file-picker :compact="true"
                           button-label="Přidat soubory"
                           button-class="btn-outline-primary btn-sm"
                           :accepted-types="SUPPORTED_ATTACHMENT_TYPES"
                           @selected="filesSelected"
              />
              <span class="small text-muted">{{ summary }}</span>
          </div>
          <p class="text-muted mb-3" v-if="isBusy">
              <span class="spinner-border spinner-border-sm me-2 align-middle" role="status"></span>
              Nahráno {{ doneCount }} z {{ totalCount }}.
          </p>
          <!-- an empty row would take the bottom padding of the card by its negative gutter margin -->
          <draggable v-if="uploadedAttachments.length > 0 || uploads.length > 0"
                     v-model="uploadedAttachments"
                     class="row g-3"
                     item-key="id"
                     handle=".photo-tile-thumb"
                     :disabled="isBusy"
                     @end="onDragEnd"
          >
              <template #item="{element: attachment, index}">
                  <div class="col-xl-2 col-lg-3 col-sm-4 col-6">
                      <attachment-control :attachment="attachment"
                                          :order="index + 1"
                                          @open="lightbox?.open(index)"
                                          @remove="removeLater([attachment], 'Soubor přílohy smazán.')"
                      />
                  </div>
              </template>
              <template #footer>
                  <div class="col-xl-2 col-lg-3 col-sm-4 col-6" v-for="upload in uploads" :key="upload.key">
                      <upload-tile :upload="upload" @retry="retry(upload.key)" @discard="discard(upload.key)"/>
                  </div>
              </template>
          </draggable>
          <photo-lightbox ref="lightbox"
                          :items="uploadedAttachments.map(attachment => ({id: attachment.id, url: attachment.url, description: ''}))"
                          :title-badge="false"
                          :can-describe="false"
                          :can-rotate="false"
                          :rotating-ids="[]"
          />
          <undo-toast :message="removalMessage" @undo="undoRemoval"/>
      </div>
  </div>
</template>
<script lang="ts">
import FilePicker from '@/components/estimates/common/controls/FilePicker.vue';
import UndoToast from '@/components/estimates/common/controls/UndoToast.vue';
import UploadTile from '@/components/estimates/common/controls/UploadTile.vue';
import PhotoLightbox from '@/components/estimates/common/photos/PhotoLightbox.vue';
import {PDF_TYPE, plural, splitSupportedFiles, SUPPORTED_IMAGE_TYPES} from '@/components/estimates/common/photos/texts';
import AttachmentControl from '@/components/estimates/expertEstimate/AttachmentControl.vue';
import {useOrderedUploads} from '@/composables/OrderedUploads';
import {usePendingRemoval} from '@/composables/PendingRemoval';
import {Attachment, AttachmentType} from '@/model/api';
import ExpertEstimate from '@/model/estimates/ExpertEstimate';
import {computed, defineComponent, inject, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import draggable from 'zhyswan-vuedraggable';

/**
 * The server saves each page of a PDF as a file, the pages take the positions up to the next file of the batch.
 * Must equal AttachmentsEndpoint::MAX_PDF_PAGES, more pages would land among the next files of the batch.
 */
const MAX_PDF_PAGES = 5;

const SUPPORTED_ATTACHMENT_TYPES = [...SUPPORTED_IMAGE_TYPES, PDF_TYPE];

export default defineComponent({
    name: 'AttachmentTypeControl',

    components: {
        AttachmentControl,
        FilePicker,
        PhotoLightbox,
        UndoToast,
        UploadTile,
        draggable,
    },

    props: {
        attachmentType: {type: String as () => AttachmentType, required: true},
        checked: {type: Boolean, required: true},
        attachments: {type: Array as () => Attachment[], required: true},
    },

    emits: ['changed'],

    setup(props, {emit}) {
        const model = inject<ExpertEstimate>('model')!;
        const {t} = useI18n();

        const uploadedAttachments = ref<Attachment[]>([...props.attachments]);
        const isAttachmentTypeSelected = ref(props.checked);

        const attachmentTypeModel = computed({
            get: () => isAttachmentTypeSelected.value,
            set: (value: boolean) => {
                handleAttachmentTypeChange(value);
                isAttachmentTypeSelected.value = value;
            },
        });

        const {uploads, isBusy, doneCount, totalCount, add, nextPosition, retry, discard, clearShown} = useOrderedUploads(
            (file: File, position: number) => model.uploadAttachment(props.attachmentType, file, position),
            () => {
                refresh();
                emit('changed');
            },
            MAX_PDF_PAGES,
        );

        const removal = usePendingRemoval(
            uploadedAttachments,
            (attachment: Attachment) => model.removeAttachment(attachment.id),
            () => {
                refresh();
                emit('changed');
            },
        );

        const lightbox = ref<InstanceType<typeof PhotoLightbox> | null>(null);
        const rejectedNames = ref<string[]>([]);

        const summary = computed(() => {
            const count = uploadedAttachments.value.length;
            const text = `${count} ${plural(count, 'soubor', 'soubory', 'souborů')}`;

            return count > 1 ? `${text}, pořadí změníte přetažením` : text;
        });

        function filesSelected(files: File[]): void {
            const {supported, rejectedNames: rejected} = splitSupportedFiles(files, SUPPORTED_ATTACHMENT_TYPES);

            rejectedNames.value = rejected;

            if (supported.length > 0) {
                const savedAttachments = [...uploadedAttachments.value, ...removal.pendingItems()];

                add(supported, nextPosition(savedAttachments.map(attachment => attachment.position)));
            }
        }

        async function onDragEnd(event: {oldIndex: number; newIndex: number}): Promise<void> {
            if (event.oldIndex === event.newIndex) {
                return;
            }

            const attachment = uploadedAttachments.value[event.newIndex];

            // the visible order matches the server only without the files waiting to be removed
            await removal.commit();
            await model.moveAttachment(attachment.id, event.newIndex);

            refresh();
        }

        function handleAttachmentTypeChange(isChecked: boolean): void {
            if (isChecked) {
                model.addAttachmentType(props.attachmentType)
                    .then(() => {
                        emit('changed', props.attachmentType);
                    });
            } else {
                model.removeAttachmentType(props.attachmentType)
                    .then(() => emit('changed'));
            }
        }

        function refresh(): void {
            model.getEstimate()
                .then(estimate => {
                    uploadedAttachments.value = estimate.attachments.filter(
                        attachment => attachment.type === props.attachmentType && ! removal.isPending(attachment.id),
                    );

                    clearShown(attachments => attachments.every(
                        attachment => uploadedAttachments.value.some(shown => shown.id === attachment.id),
                    ));
                });
        }

        function translateType(type: AttachmentType): string {
            return t(`parameters.attachments.options.${type}`);
        }

        return {
            MAX_PDF_PAGES,
            SUPPORTED_ATTACHMENT_TYPES,
            uploads,
            isBusy,
            doneCount,
            totalCount,
            uploadedAttachments,
            attachmentTypeModel,
            lightbox,
            rejectedNames,
            summary,
            removalMessage: removal.message,
            removeLater: removal.removeLater,
            undoRemoval: removal.undo,
            filesSelected,
            retry,
            discard,
            onDragEnd,
            translateType,
        };
    },
});
</script>
